import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';

/** Cancels a draft nobody will use. Best effort: a failure is ignored and nothing waits for it. */
/** The backend did not start the campaign for these statuses; anything else may have. */
const isDefiniteRefusal = (err) => err?.status === 409 || err?.status === 422 || err?.status === 400;

export const MAY_HAVE_STARTED = 'The send may have started. Check the Campaigns tab before trying again.';

const cancelQuietly = (id) => {
  try {
    Promise.resolve(adminWhatsAppService.cancelCampaign(id)).catch(() => {});
  } catch {
    // a synchronous failure is just as unimportant as a rejected promise
  }
};

/**
 * The draft campaign behind step 4. The backend can only test-send an existing campaign, so the
 * first test creates the draft and every later test reuses it. A draft carries the fingerprint of
 * the inputs it was created from and a `tested` flag; when the inputs change the draft is stale:
 * it is cancelled (best effort), the tested flag goes with it, and the next test creates a new one.
 * Only a tested draft whose fingerprint still matches can be sent.
 *
 * The draft also keeps what the server built: `queued` is how many people the campaign will really
 * message. The recipient list is snapshotted when the draft is created, which can be well after the
 * preview, so this count (not the preview's) is what the admin must confirm.
 *
 * Once sendCampaign/scheduleCampaign has been called for a draft it is never auto-cancelled again,
 * regardless of the response: cancelling also stops a campaign that is already sending, and even a
 * 409/422 can mean the campaign already started or changed concurrently, not only that nothing
 * happened. The definitive/ambiguous distinction still drives what the admin sees: a definitive
 * refusal (400, 409 or 422 - all guaranteed by the backend to mean "rejected before any state
 * changed") just shows the error and leaves the draft sendable again; any other failure (network,
 * 5xx) may have started the send, so the draft is kept, flagged `mayHaveStarted`, and cannot be sent
 * again from here. Either way the wizard's auto-cancel paths simply leave the old draft alone and
 * move on to a fresh one when the inputs change.
 *
 * Create, test and send each ignore a second call while one is in flight (a ref, so a double
 * click within one render cannot slip through).
 *
 * @param {{body: object | null, fingerprint: string, onSent?: (id: string) => void}} args
 */
export default function useCampaignDraft({ body, fingerprint, onSent }) {
  const toast = useToast();
  const [draft, setDraft] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testError, setTestError] = useState('');
  const busyRef = useRef({ test: false, send: false });
  const attemptedIds = useRef(new Set()); // drafts sendCampaign was called for; never auto-cancel these
  const draftRef = useRef(null);

  useEffect(() => {
    draftRef.current = draft;
  });

  // Stale draft: inputs changed after it was created. A draft whose send was attempted is only
  // dropped (the server keeps whatever state it gave that campaign); nothing is cancelled mid-flight.
  useEffect(() => {
    if (!draft || draft.fingerprint === fingerprint || testing || sending) return;
    if (!attemptedIds.current.has(draft.id)) cancelQuietly(draft.id);
    setDraft(null);
  }, [draft, fingerprint, testing, sending]);

  // Leaving the wizard with a draft that was never sent must not leave it lying in the campaigns list.
  useEffect(
    () => () => {
      const open = draftRef.current;
      if (open && !attemptedIds.current.has(open.id)) cancelQuietly(open.id);
    },
    [],
  );

  const current = draft && draft.fingerprint === fingerprint ? draft : null;

  const sendTest = async ({ phone, country }) => {
    if (busyRef.current.test || busyRef.current.send || !body || current?.mayHaveStarted) return;
    busyRef.current.test = true;
    setTesting(true);
    setTestError('');
    try {
      let target = current;
      if (!target) {
        const created = await adminWhatsAppService.createCampaign(body);
        const counts = created.counts || {};
        target = {
          id: created.id,
          fingerprint,
          tested: false,
          queued: counts.queued ?? 0,
          skipped: counts.skipped ?? 0,
          mayHaveStarted: false,
        };
        setDraft(target);
      }
      await adminWhatsAppService.testSend(target.id, country ? { phone, country } : { phone });
      setDraft({ ...target, tested: true });
      toast.success('Test message sent');
    } catch (err) {
      setTestError(err.message);
      toast.error(err.message);
    } finally {
      busyRef.current.test = false;
      setTesting(false);
    }
  };

  /** Starts the tested draft. Resolves when the request finished, whatever the outcome. */
  const send = async () => {
    if (busyRef.current.send || busyRef.current.test || !current?.tested || current.mayHaveStarted) return;
    const target = current;
    busyRef.current.send = true;
    attemptedIds.current.add(target.id);
    setSending(true);
    try {
      await adminWhatsAppService.sendCampaign(target.id);
      toast.success('Campaign started');
      onSent?.(target.id);
    } catch (err) {
      if (isDefiniteRefusal(err)) {
        // The mark stays: a 409/422 can also mean it already started or changed concurrently, so the
        // wizard's auto-cancel paths must still leave this draft alone. Send re-enables regardless,
        // since mayHaveStarted (the only thing send() checks) is never set for a definitive refusal.
        toast.error(err.message);
      } else {
        setDraft((d) => (d && d.id === target.id ? { ...d, mayHaveStarted: true } : d));
        toast.error(MAY_HAVE_STARTED);
      }
    } finally {
      busyRef.current.send = false;
      setSending(false);
    }
  };

  /** Schedules the tested draft for a future instant. Same success/failure handling as send(). */
  const schedule = async (scheduledAtIso) => {
    if (busyRef.current.send || busyRef.current.test || !current?.tested || current.mayHaveStarted) return;
    const target = current;
    busyRef.current.send = true;
    attemptedIds.current.add(target.id);
    setSending(true);
    try {
      await adminWhatsAppService.scheduleCampaign(target.id, scheduledAtIso);
      toast.success('Campaign scheduled');
      onSent?.(target.id);
    } catch (err) {
      if (isDefiniteRefusal(err)) {
        toast.error(err.message);
      } else {
        setDraft((d) => (d && d.id === target.id ? { ...d, mayHaveStarted: true } : d));
        toast.error(MAY_HAVE_STARTED);
      }
    } finally {
      busyRef.current.send = false;
      setSending(false);
    }
  };

  return {
    draft: current,
    testing,
    sending,
    testError,
    tested: Boolean(current?.tested),
    queued: current ? current.queued : null,
    mayHaveStarted: Boolean(current?.mayHaveStarted),
    sendTest,
    send,
    schedule,
  };
}
